<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink, type RouteLocationRaw } from 'vue-router';
import ListItem from '@/components/ui/ListItem.vue';
import RouteBadge from '@/components/ui/RouteBadge.vue';
import type { Route, Stop } from '@/@types/gtfs-ressource';
import type { RecentSearch } from '@/libs/helpers/recent-searches';
import { AppRoute } from '@/libs/routing';

/**
 * One stop or one line, as a search result or a recent search. `selectable` hands
 * the item back through `select` instead of opening its page: a stop picker.
 */
const props = defineProps<{
  route?: Route;
  stop?: Stop;
  selectable?: boolean;
}>();

const emit = defineEmits<{ open: [entry: RecentSearch]; select: [entry: RecentSearch] }>();

interface Target {
  entry: RecentSearch;
  to: RouteLocationRaw;
}

const target = computed<Target | undefined>(() => {
  if (props.stop) {
    return {
      entry: { type: 'stop', id: props.stop.stop_id },
      to: { name: AppRoute.STOP_DETAILED, params: { stopId: props.stop.stop_id } },
    };
  }
  if (props.route) {
    return {
      entry: { type: 'route', id: props.route.route_id },
      to: { name: AppRoute.ROUTE_DETAILED, params: { routeId: props.route.route_id } },
    };
  }
  return undefined;
});

function onClick(entry: RecentSearch) {
  if (props.selectable) emit('select', entry);
  else emit('open', entry);
}

const routeLabel = computed<string>(
  () => props.route?.route_long_name || props.route?.route_short_name || '',
);
</script>

<template>
  <component
    :is="selectable ? 'button' : RouterLink"
    v-if="target"
    class="search-result-item"
    :type="selectable ? 'button' : undefined"
    :replace="selectable ? undefined : true"
    :to="selectable ? undefined : target.to"
    @click="onClick(target.entry)"
  >
    <ListItem
      v-if="stop"
      type="recent-search"
      icon="square-square"
      :label="stop.stop_name"
      :description="$t('search.typeStop')"
    />
    <ListItem
      v-else-if="route"
      type="recent-search"
      icon="route"
      :label="routeLabel"
      :description="$t('search.typeRoute')"
    >
      <template #badges>
        <RouteBadge :route="route" size="small" />
      </template>
    </ListItem>
  </component>
</template>

<style lang="scss">
.search-result-item {
  background: none;
  border: none;
  display: block;
  padding: 0;
  text-align: left;
  width: 100%;

  &:active .list-item {
    background-color: $color-neutral-lower;
  }

  .list-item__icon {
    color: $color-neutral-highest;
  }

  .list-item__label {
    color: $color-neutral-highest;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
